'use client';

import { useRef } from 'react';
import { gsap, ScrollTrigger, SplitText, useGSAP, MOTION_OK } from './gsap';

/**
 * A heading whose words rise out of their own line masks when it scrolls into
 * view. Under reduced motion the heading is simply rendered as written.
 *
 * One ScrollTrigger per heading, created once. `autoSplit` re-splits whenever
 * a font finishes loading or the width changes, and it must NOT create a
 * trigger each time: every new ScrollTrigger queues a forced, page-wide
 * refresh, and a refresh in the middle of a touch scroll puts the page back
 * where it was a moment ago — the reader is thrown backwards. So the re-split
 * only re-hides the words (if they have not been shown yet), and once the
 * reveal has played the split is dropped altogether.
 */
export default function SplitReveal({ as: Tag = 'h2', className, children, delay = 0 }) {
  const ref = useRef(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        let revealed = false;
        let split = null;

        split = SplitText.create(el, {
          type: 'words,lines',
          mask: 'lines',
          linesClass: 'split-line',
          autoSplit: true,
          onSplit: (self) => {
            if (!revealed) gsap.set(self.words, { yPercent: 115 });
          },
        });

        const trigger = ScrollTrigger.create({
          trigger: el,
          start: 'top 88%',
          once: true,
          onEnter: () => {
            revealed = true;
            gsap.to(split.words, {
              yPercent: 0,
              duration: 0.9,
              ease: 'expo.out',
              stagger: 0.04,
              delay,
              // back to plain text: nothing left to re-split or re-measure
              onComplete: () => split.revert(),
            });
          },
        });

        return () => {
          trigger.kill();
          split.revert();
        };
      });
      return () => mm.revert();
    },
    { scope: ref }
  );

  return (
    <Tag ref={ref} className={className}>
      {children}
    </Tag>
  );
}
